import {
    IFileTaskSingleVoyage,
    ITaskExecutionSingleVoyage,
    ITaskSingleVoyage,
    StatusTaskExecutionSingleVoyage,
    StatusTaskSingleVoyage,
    SubTaskCreationSingleVoyage,
    SubTaskSingleVoyage,
    TaskCreationSingleVoyage,
    TERMINAL_TASK_STATUSES_SINGLE_VOYAGE
} from "@containers/singleVoyage/tasks/types";

type AnyTaskSingleVoyage = ITaskSingleVoyage | SubTaskSingleVoyage

/**
 * Stops of a task. The backend returns waypointIds as a projection of the task's
 * own executions - there is no separate store for the planned scope, one source of truth.
 */
export const getWaypointIdsTaskSingleVoyage = (task?: AnyTaskSingleVoyage): number[] => {
    if (task?.waypointIds?.length) {
        return task.waypointIds
    }
    if (!task?.executions?.length) {
        return []
    }
    return task.executions.map((execution) => execution.waypointId)
}

/**
 * A task's stops are its OWN stops, not the union with its children's.
 * A subtask works on a subset of the same stops, so there is no reason to
 * widen the parent's scope because of it.
 */
export const getAllWaypointIdsTaskSingleVoyage = (task?: ITaskSingleVoyage): number[] => {
    return getWaypointIdsTaskSingleVoyage(task)
}

export const hasWaypointTaskSingleVoyage = (task: AnyTaskSingleVoyage, waypointId: number): boolean => {
    return task?.executions?.some((execution) => execution.waypointId === Number(waypointId)) ?? false
}

// DEPRECATED: narrowing a task in the data layer is no longer needed.
// A stop holds the whole task, and the choice of "this stop only / all stops"
// is made by the view through narrowToWaypoint. Otherwise the right-hand panel
// could not show that the task also applies to other stops.
//
// export const getExecutionsByWaypointSingleVoyage = (
//     task: AnyTaskSingleVoyage,
//     waypointId: number
// ): ITaskExecutionSingleVoyage[] => {
//     if (!task?.executions?.length) {
//         return []
//     }
//     return task.executions.filter((execution) => execution.waypointId === Number(waypointId))
// }
//
// export const narrowTaskToWaypointSingleVoyage = (
//     task: ITaskSingleVoyage,
//     waypointId: number
// ): ITaskSingleVoyage => {
//     return {
//         ...task,
//         executions: getExecutionsByWaypointSingleVoyage(task, waypointId),
//         subtasks: task.subtasks?.filter((subtask) => hasWaypointTaskSingleVoyage(subtask, waypointId))
//             ?.map((subtask) => ({
//                 ...subtask,
//                 executions: getExecutionsByWaypointSingleVoyage(subtask, waypointId),
//             })) ?? [],
//     }
// }

/** A task touches a stop if the task itself or any of its subtasks does. */
export const taskTouchesWaypointSingleVoyage = (task: ITaskSingleVoyage, waypointId: number): boolean => {
    if (hasWaypointTaskSingleVoyage(task, waypointId)) {
        return true
    }
    return task?.subtasks?.some((subtask) => hasWaypointTaskSingleVoyage(subtask, waypointId)) ?? false
}

// DEPRECATED: never called. "Task is closed" means a terminal status, checked
// directly against TERMINAL_TASK_STATUSES_SINGLE_VOYAGE where it is needed.
//
// export const isClosedTaskSingleVoyage = (status?: StatusTaskSingleVoyage): boolean => {
//     return !!status && TERMINAL_TASK_STATUSES_SINGLE_VOYAGE.includes(status)
// }

/**
 * All files of a task. A photo is attached to the execution at a specific stop,
 * so wherever the task is shown as a whole (the tasks table) they must be merged.
 */
export const collectFilesTaskSingleVoyage = (task?: AnyTaskSingleVoyage): IFileTaskSingleVoyage[] => {
    if (!task?.executions?.length) {
        return []
    }
    return task.executions
        .filter((execution) => execution.status !== 'DELETED')
        .flatMap((execution) => execution.files ?? [])
}

/** How many executions are still open - for the "N of M will be closed" confirmation. */
export const countOpenExecutionsTaskSingleVoyage = (task: ITaskSingleVoyage): {
    open: number,
    total: number
} => {
    // Children = own executions PLUS subtasks: a task does not lose its own work
    // just because it gained a child.
    const leaves: AnyTaskSingleVoyage[] = [task, ...(task?.subtasks ?? [])]
    const executions = leaves.flatMap((leaf) => leaf?.executions ?? [])
        .filter((execution) => execution.status !== 'DELETED')

    return {
        open: executions.filter((execution) => execution.status === 'PLANNED').length,
        total: executions.length,
    }
}

/**
 * API task -> form value.
 *
 * The form still works in terms of waypointIds: the backend accepts them on create
 * and update and syncs the executions itself. Here we just unfold executions back.
 */
export const apiSubTaskToCreationSingleVoyage = (subtask: SubTaskSingleVoyage): SubTaskCreationSingleVoyage => {
    return {
        id: subtask.id,
        title: subtask.title,
        description: subtask.description,
        driverId: subtask.driverId,
        waypointIds: getWaypointIdsTaskSingleVoyage(subtask),
        planCompletionTime: subtask.planCompletionTime,
        status: subtask.status,
        taskConfigTemplateId: subtask.taskConfigTemplateId,
        type: 'REGULAR_SUBTASK',
    }
}

export const apiTaskToCreationSingleVoyage = (task: ITaskSingleVoyage): TaskCreationSingleVoyage => {
    return {
        id: task.id,
        accountId: task.accountId,
        title: task.title,
        description: task.description,
        driverId: task.driverId,
        waypointIds: getAllWaypointIdsTaskSingleVoyage(task),
        planCompletionTime: task.planCompletionTime,
        status: task.status,
        taskConfigTemplateId: task.taskConfigTemplateId,
        type: 'REGULAR',
        subtasks: task.subtasks?.map(apiSubTaskToCreationSingleVoyage) ?? [],
    }
}

/**
 * Form value -> task for preview.
 *
 * Needed in voyage planning mode, where the task is not saved yet and physically
 * has no executions. Execution ids are negative - they exist only for rendering,
 * and statuses cannot be changed in this mode.
 */
export const creationTaskToPreviewSingleVoyage = (
    task: TaskCreationSingleVoyage
): ITaskSingleVoyage => {
    const buildExecutions = (
        waypointIds: number[] = [],
        status: StatusTaskSingleVoyage
    ): ITaskExecutionSingleVoyage[] => waypointIds.map((waypointId, index) => ({
        id: -(index + 1),
        waypointId,
        status: status === 'COMPLETED' || status === 'CANCELLED' || status === 'DELETED'
            ? status
            : 'PLANNED',
        completionTime: null,
        updatedById: null,
        updatedDatetime: null,
        version: 1,
        files: [],
    }))

    return {
        ...(task as unknown as ITaskSingleVoyage),
        id: task.id ?? 0,
        executions: buildExecutions(task.waypointIds, task.status),
        subtasks: task.subtasks?.map((subtask) => ({
            ...(subtask as unknown as SubTaskSingleVoyage),
            id: subtask.id ?? 0,
            executions: buildExecutions(subtask.waypointIds, subtask.status),
        })) ?? [],
    }
}

/**
 * Status to the class the state mark reads. One place, because the mark appears
 * on a stop card, on a subtask row and on the edge of a task block, and those
 * three drifting apart is how a colour stops meaning anything.
 */
export type TaskStateSingleVoyage = '' | 'done' | 'failed' | 'partial' | 'running'

export const taskStateSingleVoyage = (
    status?: StatusTaskSingleVoyage | StatusTaskExecutionSingleVoyage | null
): TaskStateSingleVoyage => {
    switch (status) {
        case 'COMPLETED':
            return 'done'
        case 'CANCELLED':
            return 'failed'
        case 'PARTIAL_COMPLETE':
            return 'partial'
        case 'IN_PROGRESS':
            return 'running'
        default:
            return ''
    }
}

/** Done / total over executions. The parent is an aggregate and is not counted. */
export const countCompletedExecutionsSingleVoyage = (tasks: ITaskSingleVoyage[]): {
    complete: number,
    total: number
} => {
    if (!tasks?.length) {
        return {complete: 0, total: 0}
    }
    const executions = tasks.flatMap((task) => {
        const leaves: AnyTaskSingleVoyage[] = [task, ...(task?.subtasks ?? [])]
        return leaves.flatMap((leaf) => leaf?.executions ?? [])
    }).filter((execution) => execution.status !== 'DELETED')

    return {
        complete: executions.filter((execution) => execution.status === 'COMPLETED').length,
        total: executions.length,
    }
}
